/**
 * /downhole/work-orders — shop work on components (fieldiq-api#882
 * `GET /work-orders`), full width. A row opens the order on its own page,
 * /downhole/work-orders/:workOrderId (WorkOrderDetailScreen), where Assign,
 * the status moves and Edit live (api#905). No panel opens beside the list:
 * split panes are banned (CLAUDE.md). A legacy `?wo=<id>` link (a teardown's
 * hand-off, a bookmark) is forwarded to that page. The Assigned filter's
 * "Unassigned" finds what a teardown opened.
 *
 * The filters (status, assignee, work type) live in the URL, so the back
 * arrow on an order's page returns to the same view.
 *
 * Orders are OPENED from the component (ComponentDetailScreen: Log inspection,
 * Open work order) and by a teardown. An inspection is a work order of an
 * inspection type; there is no separate inspections screen.
 *
 * THE VOCABULARY IS THE API'S: open, in_progress, awaiting_parts, completed,
 * cancelled. The old screen used complete / approved — `?status=approved` is a
 * 400 — and showed any other status as Open.
 *
 * The totals are the API's `total_cost`, shown as it is; no line is extended.
 *
 * THE CHIP COUNTS ARE A TALLY of the one unfiltered list, which is honest
 * here: the list is unpaginated and no endpoint returns per-status counts.
 * The Unassigned filter and its count are the API's own `?unassigned=true`
 * (api#914) — a second read, so the figure is the server's selection rather
 * than a client one; the status and type filters then narrow those rows the
 * same way they narrow the full list.
 * The status chips filter that same list on the client, so a count and the
 * rows under it always come from the same read. Open and overdue on the band
 * are /dashboard's figures.
 */
import React, { useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlarmClock, ChevronRight, ClipboardList, Hammer, Loader2, PackageSearch } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeDashboard, useDownholeWorkOrders } from '../../hooks/useDownhole';
import { formatInstantDay, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { DH_CATEGORY_LABEL, DH_PRIORITY_LABEL, DH_WORK_ORDER_STATUS_SKIN, DH_WORK_TYPE_LABEL, dhLabel, dhSkin } from '../../lib/downhole-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_WORK_ORDER_STATUSES, DH_WORK_TYPES } from '../../types/downhole';
import { DH_INPUT, DhBadge, DhEmpty, DhPanel } from './DownholeBits';
import { useOpenRecord } from './recordPage';

export default function WorkOrdersScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const type = searchParams.get('type') ?? '';
  // `?assigned=none` — the orders nobody has been given, read from the API's
  // `unassigned=true`.
  const assigned = searchParams.get('assigned') === 'none' ? 'none' : '';
  const legacyWo = searchParams.get('wo');

  const query = useDownholeWorkOrders();
  const unassignedQuery = useDownholeWorkOrders({ unassigned: 'true' });
  // The register shows whichever read the Assigned filter picks; its loading
  // and failure are that read's, never the other's.
  const listQuery = assigned === 'none' ? unassignedQuery : query;
  const listFailure = queryLoadFailure(listQuery);
  const dashboardQuery = useDownholeDashboard();
  const failure = queryLoadFailure(query);
  const all = query.data?.data ?? [];
  const meta = query.data?.meta;
  const tz = meta?.time_zone ?? null;
  const currency = meta?.currency ?? null;
  const dashboard = dashboardQuery.data?.data ?? null;
  const loaded = !query.isLoading && !failure;

  const rows = useMemo(
    () =>
      (listQuery.data?.data ?? []).filter((w) => (!status || w.status === status) && (!type || w.work_type === type)),
    [listQuery.data, status, type],
  );
  const tally = (s: string) => (loaded ? all.filter((w) => w.status === s).length : undefined);
  // The API's unassigned rows, counted — exactly what the filter shows before a
  // status chip narrows it. No number while that read is out or failed.
  const unassignedCount =
    unassignedQuery.data && !queryLoadFailure(unassignedQuery) ? unassignedQuery.data.data.length : undefined;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const open = (id: string) => openRecord(`/downhole/work-orders/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  const chips = [
    { key: '', label: 'All', count: loaded ? all.length : undefined },
    ...DH_WORK_ORDER_STATUSES.map((s) => ({ key: s, label: dhSkin(DH_WORK_ORDER_STATUS_SKIN, s).label, count: tally(s) })),
  ];

  if (legacyWo) return <Navigate to={`/downhole/work-orders/${legacyWo}`} replace />;

  return (
    <div data-testid="downhole-work-orders-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Work Orders"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            // /dashboard's open_work_orders counts open, in progress AND
            // awaiting parts. Labelled "Active" so it does not contradict the
            // Open chip below, which is the `open` status alone.
            label: 'Active',
            sub: 'Not finished',
            tone: 'accent',
            loading: dashboardQuery.isLoading,
            value: displayValue(dashboard?.open_work_orders),
          },
          {
            icon: <Hammer className="size-4" aria-hidden="true" />,
            label: 'In progress',
            sub: 'On the bench',
            tone: 'info',
            loading: query.isLoading,
            value: displayValue(tally('in_progress')),
          },
          {
            icon: <AlarmClock className="size-4" aria-hidden="true" />,
            label: 'Overdue',
            sub: 'Past due',
            tone: 'accent',
            loading: dashboardQuery.isLoading,
            value: displayValue(dashboard?.overdue_work_orders),
          },
          {
            icon: <PackageSearch className="size-4" aria-hidden="true" />,
            label: 'Awaiting parts',
            sub: 'Blocked',
            tone: 'neutral',
            loading: query.isLoading,
            value: displayValue(tally('awaiting_parts')),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto lg:flex-wrap" role="group" aria-label="Filter by status">
              {chips.map((chip) => {
                const active = status === chip.key;
                return (
                  <button
                    key={chip.key || 'all'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setParam('status', chip.key)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {chip.label}
                    {chip.count !== undefined && (
                      <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{chip.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <select
              className={`${DH_INPUT} lg:w-56`}
              aria-label="Filter by assignee"
              value={assigned}
              onChange={(e) => setParam('assigned', e.target.value)}
            >
              <option value="">Anyone or no one</option>
              <option value="none">Unassigned{unassignedCount !== undefined ? ` (${unassignedCount})` : ''}</option>
            </select>
            <select
              className={`${DH_INPUT} lg:w-56`}
              aria-label="Filter by work type"
              value={type}
              onChange={(e) => setParam('type', e.target.value)}
            >
              <option value="">All work types</option>
              {DH_WORK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DH_WORK_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>

          <DhPanel
            eyebrow="Shop"
            title="Work orders"
            testId="downhole-work-order-register"
            footer={
              loaded && !listFailure && listQuery.data && rows.length > 0 ? `Showing ${rows.length} of ${all.length} work orders` : undefined
            }
          >
            {listQuery.isLoading || (!listFailure && !listQuery.data) ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading work orders" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="work orders" />
            ) : rows.length === 0 ? (
              <DhEmpty>{status || type || assigned ? 'No work order matches these filters.' : 'There are no work orders.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-3 py-3">WO #</th>
                        <th scope="col" className="px-3 py-3">Component</th>
                        <th scope="col" className="px-3 py-3">Type</th>
                        <th scope="col" className="px-3 py-3">Status</th>
                        <th scope="col" className="px-3 py-3">Priority</th>
                        <th scope="col" className="px-3 py-3">Assigned to</th>
                        <th scope="col" className="px-3 py-3">Due</th>
                        <th scope="col" className="px-3 py-3 text-right">Total</th>
                        <th scope="col" className="w-[44px] px-3 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((w) => (
                        <tr
                          key={w.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(w.id)}
                          onKeyDown={rowKeys(w.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            dhSkin(DH_WORK_ORDER_STATUS_SKIN, w.status).rail
                          }`}
                        >
                          <td className="whitespace-nowrap px-3 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{w.wo_number || '—'}</td>
                          <td className="px-3 py-3">
                            <p className="whitespace-nowrap font-mono text-sm text-white">{w.component_serial || '—'}</p>
                            <p className="text-xs text-fieldiq-muted">{dhLabel(DH_CATEGORY_LABEL, w.component_category)}</p>
                          </td>
                          <td className="px-3 py-3 text-sm text-fieldiq-text">{dhLabel(DH_WORK_TYPE_LABEL, w.work_type)}</td>
                          <td className="px-3 py-3"><DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={w.status} /></td>
                          <td className="px-3 py-3 text-sm text-fieldiq-text">{dhLabel(DH_PRIORITY_LABEL, w.priority)}</td>
                          {/* Nobody assigned is a fact about the order, not an absent value
                              — so it is said, not drawn as a dash. A set assignee whose
                              name did not come back is the absent case: `—`. */}
                          <td className="px-3 py-3 text-sm text-fieldiq-text">
                            {w.assigned_to ? displayValue(w.assigned_to_name) : <span className="text-fieldiq-muted">Not assigned</span>}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-sm text-fieldiq-text">{formatInstantTenantDay(w.due_date, tz) ?? '—'}</td>
                          <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(w.total_cost, currency)}</td>
                          <td className="px-3 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((w) => (
                    <div
                      key={w.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(w.id)}
                      onKeyDown={rowKeys(w.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{w.wo_number || '—'}</p>
                        <DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={w.status} />
                      </div>
                      <p className="mt-1 text-sm text-white">
                        {dhLabel(DH_WORK_TYPE_LABEL, w.work_type)} · <span className="font-mono">{w.component_serial || '—'}</span>
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        Due {formatInstantTenantDay(w.due_date, tz) ?? '—'} · opened {formatInstantDay(w.created_at, tz) ?? '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>

        </div>
      </section>
    </div>
  );
}
